/**
 * CEO Command Center - Storage Service
 * 
 * Handles all localStorage operations with:
 * - Type-safe get/set operations
 * - JSON serialization/deserialization
 * - Error handling for quota exceeded
 * - Migration support for schema changes
 * 
 * @module services/storage
 * @version 10.0.0
 */

import { APP_CONFIG } from '../config/constants';
import type { 
  User, 
  UserConfig, 
  Investor, 
  MarketNews,
  MAURecord,
  CustomerMAU,
  BoardMember,
  Initiative,
  ChatMessage,
} from '../types';

// ============================================
// TYPES
// ============================================

/**
 * Storage key type for type safety
 */
type StorageKey = keyof typeof APP_CONFIG.STORAGE_KEYS;

/**
 * Mapping of storage keys to their value types
 */
interface StorageTypes {
  AUTH: User | null;
  CONFIG: UserConfig;
  CHAT: ChatMessage[];
  AI_PROMPT: string;
  KNOWLEDGE_BASE: Array<{ id: string; name: string; content: string }>;
  AIA_TASKS: Array<{
    id: string;
    title: string;
    owner: string;
    status: string;
    priority: string;
    targetDate: string;
    track: string;
    notes?: string;
  }>;
  BOARD_CONFIG: {
    nextMeetingDate?: string;
    meetingCadence?: string;
    goals?: string[];
  };
  DECK_SECTIONS: Array<{
    id: string;
    title: string;
    required: boolean;
    status: 'ready' | 'pending' | 'not-started';
  }>;
  INITIATIVES: Initiative[];
  BOARD_MEMBERS: BoardMember[];
  MEETING_HISTORY: Array<{
    date: string;
    attendees: string[];
    notes: string;
  }>;
  INVESTORS: Investor[];
  MARKET_NEWS: MarketNews[];
  NEWS_API_CONFIG: {
    apiKey?: string;
    keywords: string[];
    autoFetch: boolean;
  };
  MAU_DATA: MAURecord[];
  CUSTOMER_MAU: CustomerMAU[];
  MAU_SUMMARY: any;  // MAUSummary type from mauImport
}

// ============================================
// STORAGE SERVICE
// ============================================

/**
 * Storage service for localStorage operations
 */
export const storageService = {
  /**
   * Get a value from localStorage
   * 
   * @param key - Storage key
   * @returns Parsed value or null if not found
   * 
   * @example
   * const user = storageService.get('AUTH');
   * const config = storageService.get('CONFIG');
   */
  get<K extends StorageKey>(key: K): StorageTypes[K] | null {
    try {
      const storageKey = APP_CONFIG.STORAGE_KEYS[key];
      const item = localStorage.getItem(storageKey);
      
      if (item === null) {
        return null;
      }
      
      return JSON.parse(item) as StorageTypes[K];
    } catch (error) {
      console.error(`Error reading from localStorage [${key}]:`, error);
      return null;
    }
  },
  
  /**
   * Set a value in localStorage
   * 
   * @param key - Storage key
   * @param value - Value to store
   * @returns Success boolean
   * 
   * @example
   * storageService.set('AUTH', { name: 'Marij' });
   * storageService.set('CONFIG', { signalTarget: 5 });
   */
  set<K extends StorageKey>(key: K, value: StorageTypes[K]): boolean {
    try {
      const storageKey = APP_CONFIG.STORAGE_KEYS[key];
      localStorage.setItem(storageKey, JSON.stringify(value));
      return true;
    } catch (error) {
      // Handle quota exceeded
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded');
        // Try to clear old data
        this.clearOldData();
        // Retry once
        try {
          const storageKey = APP_CONFIG.STORAGE_KEYS[key];
          localStorage.setItem(storageKey, JSON.stringify(value));
          return true;
        } catch {
          return false;
        }
      }
      console.error(`Error writing to localStorage [${key}]:`, error);
      return false;
    }
  },
  
  /**
   * Remove a value from localStorage
   * 
   * @param key - Storage key
   */
  remove(key: StorageKey): void {
    try {
      const storageKey = APP_CONFIG.STORAGE_KEYS[key];
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Error removing from localStorage [${key}]:`, error);
    }
  },
  
  /**
   * Check if a key exists in localStorage
   * 
   * @param key - Storage key
   * @returns True if key exists
   */
  has(key: StorageKey): boolean {
    try {
      const storageKey = APP_CONFIG.STORAGE_KEYS[key];
      return localStorage.getItem(storageKey) !== null;
    } catch {
      return false;
    }
  },
  
  /**
   * Get a value with a default fallback
   * 
   * @param key - Storage key
   * @param defaultValue - Default value if not found
   * @returns Stored value or default
   */
  getOrDefault<K extends StorageKey>(key: K, defaultValue: StorageTypes[K]): StorageTypes[K] {
    const value = this.get(key);
    return value !== null ? value : defaultValue;
  },
  
  /**
   * Update a value by merging with existing data
   * Useful for partial updates to objects
   * 
   * @param key - Storage key
   * @param updates - Partial updates to merge
   * @returns Updated value
   */
  update<K extends StorageKey>(
    key: K, 
    updates: Partial<StorageTypes[K]>
  ): StorageTypes[K] | null {
    const current = this.get(key);
    
    if (current === null) {
      return null;
    }
    
    if (typeof current === 'object' && !Array.isArray(current)) {
      const updated = { ...current, ...updates } as StorageTypes[K];
      this.set(key, updated);
      return updated;
    }
    
    return current;
  },
  
  /**
   * Append an item to an array in storage
   * 
   * @param key - Storage key (must be array type)
   * @param item - Item to append
   * @param maxItems - Maximum items to keep (oldest removed first)
   */
  append<K extends StorageKey>(
    key: K,
    item: StorageTypes[K] extends Array<infer T> ? T : never,
    maxItems?: number
  ): void {
    const current = this.get(key) as unknown[];
    const array = Array.isArray(current) ? current : [];
    
    array.push(item);
    
    // Limit array size if specified
    if (maxItems && array.length > maxItems) {
      array.splice(0, array.length - maxItems);
    }
    
    this.set(key, array as StorageTypes[K]);
  },
  
  /**
   * Clear all CEO Command Center data
   * Use with caution!
   */
  clearAll(): void {
    Object.values(APP_CONFIG.STORAGE_KEYS).forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore errors during clear
      }
    });
  },
  
  /**
   * Clear old/stale data to free up space
   * Called automatically when quota is exceeded
   */
  clearOldData(): void {
    // Clear chat history (can be regenerated)
    this.remove('CHAT');
    
    // Clear market news (can be refetched)
    this.remove('MARKET_NEWS');
    
    // Trim knowledge base to most recent 3 items
    const kb = this.get('KNOWLEDGE_BASE');
    if (kb && kb.length > 3) {
      this.set('KNOWLEDGE_BASE', kb.slice(-3));
    }
  },
  
  /**
   * Export all data as JSON
   * Useful for backup/migration
   * 
   * @returns All stored data as JSON string
   */
  exportAll(): string {
    const data: Record<string, unknown> = {};
    
    Object.entries(APP_CONFIG.STORAGE_KEYS).forEach(([key, storageKey]) => {
      try {
        const item = localStorage.getItem(storageKey);
        if (item) {
          data[key] = JSON.parse(item);
        }
      } catch {
        // Skip invalid entries
      }
    });
    
    return JSON.stringify(data, null, 2);
  },
  
  /**
   * Import data from JSON
   * Useful for restore/migration
   * 
   * @param json - JSON string to import
   * @returns Success boolean
   */
  importAll(json: string): boolean {
    try {
      const data = JSON.parse(json) as Record<string, unknown>;
      
      Object.entries(data).forEach(([key, value]) => {
        if (key in APP_CONFIG.STORAGE_KEYS) {
          this.set(key as StorageKey, value as StorageTypes[StorageKey]);
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  },
  
  /**
   * Get storage usage statistics
   * 
   * @returns Storage statistics
   */
  getStats(): { used: number; total: number; percentage: number } {
    let used = 0;
    
    Object.values(APP_CONFIG.STORAGE_KEYS).forEach(key => {
      const item = localStorage.getItem(key);
      if (item) {
        used += item.length * 2; // UTF-16 encoding = 2 bytes per char
      }
    });
    
    // Typical localStorage limit is 5MB
    const total = 5 * 1024 * 1024;
    
    return {
      used,
      total,
      percentage: Math.round((used / total) * 100),
    };
  },
};

// ============================================
// SPECIALIZED STORAGE HELPERS
// ============================================

/**
 * Auth storage helpers
 */
export const authStorage = {
  getUser(): User | null {
    return storageService.get('AUTH');
  },
  
  setUser(user: User): void {
    storageService.set('AUTH', user);
  },
  
  clearUser(): void {
    storageService.remove('AUTH');
  },
  
  isLoggedIn(): boolean {
    return storageService.has('AUTH');
  },
};

/**
 * Config storage helpers
 */
export const configStorage = {
  getConfig(): UserConfig | null {
    return storageService.get('CONFIG');
  },
  
  setConfig(config: UserConfig): void {
    storageService.set('CONFIG', config);
  },
  
  updateConfig(updates: Partial<UserConfig>): void {
    const current = this.getConfig() || {
      signalTarget: 5,
      showNoise: true,
      theme: 'light' as const,
      refreshInterval: 0,
      notifications: true,
      compactMode: false,
    };
    storageService.set('CONFIG', { ...current, ...updates });
  },
};

/**
 * Chat storage helpers
 */
export const chatStorage = {
  getHistory(): ChatMessage[] {
    return storageService.get('CHAT') || [];
  },
  
  addMessage(message: ChatMessage): void {
    storageService.append('CHAT', message, 50); // Keep last 50 messages
  },
  
  clearHistory(): void {
    storageService.remove('CHAT');
  },
  
  getSystemPrompt(): string {
    return storageService.get('AI_PROMPT') || '';
  },
  
  setSystemPrompt(prompt: string): void {
    storageService.set('AI_PROMPT', prompt);
  },
};

/**
 * Investors storage helpers
 */
export const investorStorage = {
  getInvestors(): Investor[] {
    return storageService.get('INVESTORS') || [];
  },
  
  setInvestors(investors: Investor[]): void {
    storageService.set('INVESTORS', investors);
  },
  
  addInvestor(investor: Investor): void {
    const investors = this.getInvestors();
    investors.push(investor);
    this.setInvestors(investors);
  },
  
  updateInvestor(id: string, updates: Partial<Investor>): void {
    const investors = this.getInvestors();
    const index = investors.findIndex(i => i.id === id);
    if (index !== -1) {
      investors[index] = { ...investors[index], ...updates };
      this.setInvestors(investors);
    }
  },
  
  deleteInvestor(id: string): void {
    const investors = this.getInvestors().filter(i => i.id !== id);
    this.setInvestors(investors);
  },
};

/**
 * MAU storage helpers
 */
export const mauStorage = {
  get(): any | null {
    return storageService.get('MAU_SUMMARY') || null;
  },
  
  set(data: any): void {
    storageService.set('MAU_SUMMARY', data);
  },
  
  clear(): void {
    storageService.remove('MAU_SUMMARY');
  },
  
  // Legacy methods for backwards compatibility
  getMAUData(): MAURecord[] {
    return storageService.get('MAU_DATA') || [];
  },
  
  setMAUData(data: MAURecord[]): void {
    storageService.set('MAU_DATA', data);
  },
  
  getCustomerMAU(): CustomerMAU[] {
    return storageService.get('CUSTOMER_MAU') || [];
  },
  
  setCustomerMAU(data: CustomerMAU[]): void {
    storageService.set('CUSTOMER_MAU', data);
  },
};

/**
 * Board storage helpers
 */
export const boardStorage = {
  getMembers(): BoardMember[] {
    return storageService.get('BOARD_MEMBERS') || [];
  },
  
  setMembers(members: BoardMember[]): void {
    storageService.set('BOARD_MEMBERS', members);
  },
  
  getInitiatives(): Initiative[] {
    return storageService.get('INITIATIVES') || [];
  },
  
  setInitiatives(initiatives: Initiative[]): void {
    storageService.set('INITIATIVES', initiatives);
  },
  
  getDeckSections(): StorageTypes['DECK_SECTIONS'] {
    return storageService.get('DECK_SECTIONS') || [];
  },
  
  setDeckSections(sections: StorageTypes['DECK_SECTIONS']): void {
    storageService.set('DECK_SECTIONS', sections);
  },
};

/**
 * News storage helpers
 */
export const newsStorage = {
  getNews(): MarketNews[] {
    return storageService.get('MARKET_NEWS') || [];
  },
  
  setNews(news: MarketNews[]): void {
    storageService.set('MARKET_NEWS', news);
  },
  
  addNews(item: MarketNews): void {
    const news = this.getNews();
    news.unshift(item); // Add to beginning
    // Keep only last 50 items
    if (news.length > 50) {
      news.splice(50);
    }
    this.setNews(news);
  },
  
  getConfig(): StorageTypes['NEWS_API_CONFIG'] | null {
    return storageService.get('NEWS_API_CONFIG');
  },
  
  setConfig(config: StorageTypes['NEWS_API_CONFIG']): void {
    storageService.set('NEWS_API_CONFIG', config);
  },
};

/**
 * AIA Tasks storage helpers (AI Automation domain)
 */
export const aiaStorage = {
  getTasks(): StorageTypes['AIA_TASKS'] {
    return storageService.get('AIA_TASKS') || [];
  },
  
  setTasks(tasks: StorageTypes['AIA_TASKS']): void {
    storageService.set('AIA_TASKS', tasks);
  },
  
  addTask(task: StorageTypes['AIA_TASKS'][0]): void {
    const tasks = this.getTasks();
    tasks.push(task);
    this.setTasks(tasks);
  },
  
  updateTask(id: string, updates: Partial<StorageTypes['AIA_TASKS'][0]>): void {
    const tasks = this.getTasks();
    const index = tasks.findIndex(t => t.id === id);
    if (index !== -1) {
      tasks[index] = { ...tasks[index], ...updates };
      this.setTasks(tasks);
    }
  },
};

// ============================================
// EXPORT
// ============================================

export default storageService;
